import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Search, Key, Shield, AlertTriangle, CheckCircle,
  RefreshCw, Eye, EyeOff, UserX, UserCheck, Mail, CreditCard, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_SECRET = 'homefixer360-db-fix-2024';

const PLAN_OPTIONS = [
  { value: '', label: 'No Plan' },
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
  { value: 'beta', label: 'Beta Tester' },
  { value: 'lead_program', label: 'Lead Program' },
];

const PLAN_COLORS = {
  'starter': 'bg-blue-100 text-blue-700',
  'pro': 'bg-orange-100 text-orange-700',
  'enterprise': 'bg-purple-100 text-purple-700',
  'beta': 'bg-green-100 text-green-700',
  'lead_program': 'bg-teal-100 text-teal-700',
};

const SuperAdminUsers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState('');

  // Password reset
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetStatus, setResetStatus] = useState(null);

  // Plan change
  const [changingPlanFor, setChangingPlanFor] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin' || user?.email === 'admin@homefixer360.com' || user?.email === 'guy@ideaguybrands.com';

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard');
    } else {
      loadAllUsers();
    }
  }, [isSuperAdmin, navigate]);

  const token = localStorage.getItem('rooftrack_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadAllUsers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/users`, { headers });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchUser = async () => {
    if (!searchEmail) { setSearchError('Please enter an email address'); return; }
    setIsLoading(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/check-user/${encodeURIComponent(searchEmail)}?admin_secret=${ADMIN_SECRET}`
      );
      setSearchResult(response.data);
      if (response.data.found) setResetEmail(response.data.email);
    } catch (error) {
      setSearchError(error.response?.data?.detail || 'Error searching for user');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!resetEmail || !newPassword) {
      setResetStatus({ success: false, message: 'Email and new password are required' });
      return;
    }
    setIsLoading(true);
    setResetStatus(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/fix-user-password`,
        { email: resetEmail, new_password: newPassword, admin_secret: ADMIN_SECRET }
      );
      setResetStatus({ success: true, message: `Password reset for ${resetEmail}` });
      setNewPassword('');
    } catch (error) {
      setResetStatus({ success: false, message: error.response?.data?.detail || 'Failed to reset password' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePlan = async (userId, newPlan) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/auth/users/${userId}/subscription`,
        { subscription_tier: newPlan || null, subscription_status: newPlan ? 'active' : 'none' },
        { headers }
      );
      toast.success(`Plan updated to ${newPlan || 'None'}`);
      setChangingPlanFor(null);
      loadAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    }
  };

  if (!isSuperAdmin) return null;

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="super-admin-users">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="w-7 h-7 text-purple-600" />
          System Users
        </h1>
        <p className="text-sm text-gray-500 mt-1">Super Administrator — Manage users, plans, and credentials</p>
      </div>

      {/* User Search */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
          <Search className="w-5 h-5 mr-2 text-blue-600" /> Search User
        </h2>
        <div className="flex gap-4 mb-4">
          <Input
            type="email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder="Enter user email address"
            onKeyDown={(e) => e.key === 'Enter' && handleSearchUser()}
            className="flex-1"
          />
          <Button onClick={handleSearchUser} disabled={isLoading}>
            {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2">Search</span>
          </Button>
        </div>

        {searchError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{searchError}</div>}

        {searchResult && (
          <div className={`border rounded-lg p-4 ${searchResult.found ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            {searchResult.found ? (
              <div>
                <div className="flex items-center mb-3">
                  <UserCheck className="w-5 h-5 text-green-600 mr-2" />
                  <span className="font-semibold text-green-800">User Found</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><Label className="text-gray-600">Email</Label><p className="font-medium">{searchResult.email}</p></div>
                  <div><Label className="text-gray-600">Name</Label><p className="font-medium">{searchResult.name || 'N/A'}</p></div>
                  <div><Label className="text-gray-600">Role</Label><p className="font-medium">{searchResult.role || 'user'}</p></div>
                  <div><Label className="text-gray-600">Active</Label><p className={`font-medium ${searchResult.is_active ? 'text-green-600' : 'text-red-600'}`}>{searchResult.is_active ? 'Yes' : 'No'}</p></div>
                  <div><Label className="text-gray-600">Has Password</Label><p className={`font-medium ${searchResult.has_password ? 'text-green-600' : 'text-red-600'}`}>{searchResult.has_password ? 'Yes' : 'No'}</p></div>
                  <div><Label className="text-gray-600">Subscription</Label><p className="font-medium">{searchResult.subscription_tier || 'N/A'}</p></div>
                </div>
              </div>
            ) : (
              <div className="flex items-center"><UserX className="w-5 h-5 text-yellow-600 mr-2" /><span className="text-yellow-800">{searchResult.message}</span></div>
            )}
          </div>
        )}
      </div>

      {/* Password Reset */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <button onClick={() => setShowPasswordReset(!showPasswordReset)} className="flex items-center gap-2 text-lg font-semibold text-gray-900 w-full">
          <Key className="w-5 h-5 text-orange-600" /> Password Reset
          <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${showPasswordReset ? 'rotate-180' : ''}`} />
        </button>
        {showPasswordReset && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>User Email</Label><Input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="user@example.com" /></div>
              <div>
                <Label>New Password</Label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <Button onClick={handlePasswordReset} disabled={isLoading || !resetEmail || !newPassword} className="bg-orange-600 hover:bg-orange-700">
              {isLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />} Reset Password
            </Button>
            {resetStatus && (
              <div className={`p-4 rounded-lg ${resetStatus.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {resetStatus.success ? <CheckCircle className="w-4 h-4 text-green-600 inline mr-2" /> : <AlertTriangle className="w-4 h-4 text-red-600 inline mr-2" />}
                <span className={resetStatus.success ? 'text-green-800' : 'text-red-800'}>{resetStatus.message}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* All Users Table */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-600" /> All Users ({users.length})
          </h2>
          <Button onClick={loadAllUsers} variant="outline" size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No users found</td></tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 text-sm text-gray-900">{u.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{u.first_name} {u.last_name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        u.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                        u.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>{u.role || 'user'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {changingPlanFor === u.id ? (
                        <div className="flex items-center gap-2">
                          <select
                            defaultValue={u.subscription_tier || ''}
                            onChange={(e) => handleChangePlan(u.id, e.target.value)}
                            className="border rounded px-2 py-1 text-sm"
                            data-testid={`plan-select-${u.id}`}
                          >
                            {PLAN_OPTIONS.map(p => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                          </select>
                          <button onClick={() => setChangingPlanFor(null)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                        </div>
                      ) : (
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-orange-300 ${PLAN_COLORS[u.subscription_tier] || 'bg-gray-100 text-gray-600'}`}
                          onClick={() => setChangingPlanFor(u.id)}
                          title="Click to change plan"
                          data-testid={`plan-badge-${u.id}`}
                        >
                          <CreditCard className="w-3 h-3 inline mr-1" />
                          {u.subscription_tier ? u.subscription_tier.charAt(0).toUpperCase() + u.subscription_tier.slice(1) : 'No Plan'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        u.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>{u.is_active !== false ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setChangingPlanFor(u.id)} title="Change plan">
                          <CreditCard className="w-3 h-3 mr-1" /> Plan
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => { setResetEmail(u.email); setShowPasswordReset(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} title="Reset password">
                          <Key className="w-3 h-3 mr-1" /> Reset
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminUsers;
